import { AlertTriangle, Check, Cpu, Loader2, Lock } from "lucide-react"
import { useEffect, useState } from "react"

import { Page } from "@/components/layout/page"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { api } from "@/lib/api"
import type { AppSettings, ModelOption, ModelRole, SettingsUpdate } from "@/lib/types"
import { cn } from "@/lib/utils"

/**
 * The developer half of the application, kept away from the working half.
 *
 * Somebody using a career assistant does not necessarily know what a
 * temperature is, and a model picker in the chat header is one mis-click away
 * from changing the thing answering you mid-conversation. So it lives on its
 * own page, reached deliberately.
 *
 * The list is closed rather than a text box, and that is a scar. An earlier
 * version accepted any model id; one that the account could not reach was set,
 * every grading call returned 404, and because grading fails open the
 * application kept answering while retrieval grading was quietly dead for days.
 * A free-text field here would be that bug with better styling.
 */

export function SettingsRoute() {
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    void api.settings
      .read()
      .then((value) => !cancelled && setSettings(value))
      .catch((error: Error) => !cancelled && setMessage(error.message))
    return () => {
      cancelled = true
    }
  }, [])

  /**
   * One writer for every change on this page.
   *
   * `busy` is keyed by what is being changed rather than being a bare boolean,
   * so choosing a model does not grey out the temperature slider and vice
   * versa – and two changes cannot race.
   */
  async function apply(key: string, patch: SettingsUpdate) {
    if (busy) return
    setBusy(key)
    setMessage(null)
    try {
      const updated = await api.settings.update(patch)
      setSettings(updated)
      setMessage("Saved for this session. It reverts to .env when the server restarts.")
    } catch (error) {
      setMessage((error as Error).message)
    } finally {
      setBusy(null)
    }
  }

  function choose(id: string) {
    void apply(id, { chatModel: id })
  }

  return (
    <Page
      tone="developer"
      plain
      eyebrow="Developer settings"
      title="Model configuration"
      description="Which model answers, and what it costs. Separate from the rest of the app on purpose."
    >
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_420px]">
        <div className="space-y-4">
        {message ? (
          <p className="rounded-lg border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
            {message}
          </p>
        ) : null}

        {settings === null ? (
          <div className="space-y-3">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        ) : (
          <>
            {settings.customModel ? (
              <p className="flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 text-sm">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-warning" aria-hidden />
                <span>
                  <span className="font-medium">
                    <code className="font-mono">{settings.chatModel}</code> was set by hand
                  </span>{" "}
                  in <code className="font-mono">.env</code> and is not one of the options below.
                  Choosing one here replaces it for this session.
                </span>
              </p>
            ) : null}

            {settings.canChange ? null : (
              <p className="flex items-start gap-3 rounded-lg border bg-card/50 px-4 py-3 text-sm text-muted-foreground">
                <Lock className="mt-0.5 size-4 shrink-0" aria-hidden />
                <span>
                  These settings apply to every account on this server, so only the operator can
                  change them. You can see here which model does which job.
                </span>
              </p>
            )}

            <section className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground">
                Model that writes the answers
              </h2>
              <div className="space-y-2">
                {settings.models.map((model) => (
                  <ModelRow
                    key={model.id}
                    model={model}
                    selected={model.id === settings.chatModel}
                    busy={busy === model.id}
                    locked={!settings.canChange}
                    onChoose={() => choose(model.id)}
                  />
                ))}
              </div>
            </section>

            <section className="space-y-4 rounded-xl border bg-card/40 px-4 py-3">
              <h2 className="text-sm font-medium">How the answer is written</h2>

              <NumberField
                label="Temperature"
                hint="0 is repeatable, 2 is erratic. A CV generator wants the low end."
                value={settings.temperature}
                min={0}
                max={2}
                step={0.1}
                format={(value) => value.toFixed(2)}
                busy={busy === "temperature"}
                locked={!settings.canChange}
                onCommit={(value) => void apply("temperature", { temperature: value })}
              />

              <NumberField
                label="Max tokens per answer"
                hint="The ceiling on one answer. Too low truncates a tailored CV mid-sentence; too high lets one turn eat the session budget."
                value={settings.maxTokens}
                min={256}
                max={8000}
                step={100}
                format={(value) => Math.round(value).toLocaleString()}
                busy={busy === "maxTokens"}
                locked={!settings.canChange}
                onCommit={(value) => void apply("maxTokens", { maxTokens: Math.round(value) })}
              />

              <dl className="space-y-1.5 text-sm">
                <Row term="Monthly ceiling" value={`$${settings.monthlyBudgetUsd.toFixed(2)}`} />
              </dl>
              <p className="text-xs leading-relaxed text-muted-foreground">
                These change the running server until it restarts – nothing on this page edits a
                file. The monthly ceiling is deliberately not editable here: a budget you can raise
                from inside the application it is meant to restrain is not a budget.
              </p>
            </section>

            {/* Kept in this column rather than beside the prices it talks
                about. The right-hand panel is already the taller of the two, so
                anything added there is what pushes the page past one screen;
                this column ends here and has the room. */}
            <p className="text-xs leading-relaxed text-muted-foreground">
              Prices are estimates for comparison; the usage meter reports what OpenRouter actually
              charged. Before relying on a model you have not used, run{" "}
              <code className="font-mono">python scripts/check_models.py</code> – it sends one
              token to each and says which answered.
            </p>
          </>
        )}
        </div>

        <aside>
          {settings ? (
            <div className="xl:sticky xl:top-6">
              <ModelDetail settings={settings} />
            </div>
          ) : (
            <Skeleton className="h-[460px] w-full" />
          )}
        </aside>
      </div>
    </Page>
  )
}

/**
 * The chosen answer model, and the one fact a price list does not give: it is
 * not the only model in the application. Each job has its own role
 * (`core/roles.py`) - the critic deliberately a different model from the
 * writer - and the table under it is the server's own, so it cannot drift
 * from the code.
 */
function ModelDetail({ settings }: { settings: AppSettings }) {
  const chosen = settings.models.find((model) => model.id === settings.chatModel)

  return (
    <div className="space-y-3 rounded-2xl border bg-card/40 p-5">
      <div>
        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-primary">
          What this model runs
        </p>
        <p className="mt-1 text-lg font-medium">{chosen?.label ?? settings.chatModel}</p>
      </div>

      {chosen ? (
        <>
          <ul className="space-y-1.5 text-sm">
            {chosen.strengths.map((item) => (
              <li key={item} className="flex items-start gap-2 text-muted-foreground">
                <Check className="mt-0.5 size-3.5 shrink-0 text-success" aria-hidden />
                {item}
              </li>
            ))}
          </ul>

          <p className="flex items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm leading-relaxed text-muted-foreground">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden />
            {chosen.caution}
          </p>
        </>
      ) : null}

      <RoleTable roles={settings.roles} />

      {chosen ? (
        <p className="text-xs leading-relaxed text-muted-foreground">
          At ${chosen.inputPerMtok.toFixed(2)} in and ${chosen.outputPerMtok.toFixed(2)} out per
          million tokens, a turn of roughly 4,000 in and 500 out costs about{" "}
          <span className="font-mono">
            ${((4000 * chosen.inputPerMtok + 500 * chosen.outputPerMtok) / 1_000_000).toFixed(4)}
          </span>
          . That is arithmetic on a typical turn, not a measurement – the usage meter reports what
          OpenRouter actually charged.
        </p>
      ) : null}
    </div>
  )
}

/**
 * Which model does which job - the server's own table (`core/roles.py`), so
 * this cannot drift from the code. Read-only: these are developer settings in
 * `.env`, and only the answer model above is chosen here.
 */
function RoleTable({ roles }: { roles: ModelRole[] }) {
  return (
    <div className="space-y-2 rounded-xl border border-border/60 bg-background/40 p-3">
      <p className="text-sm font-medium">Which model does which job</p>
      <ul className="space-y-2">
        {roles.map((role) => (
          <li key={role.name} className="text-sm">
            <span className="flex flex-wrap items-baseline justify-between gap-x-2">
              <span className="font-medium">{role.label}</span>
              <span className="font-mono text-xs">{role.model}</span>
            </span>
            <span className="block text-xs text-muted-foreground">
              {role.does}
              {role.followsAnswer ? " Uses the answer model." : null}
            </span>
          </li>
        ))}
      </ul>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Set in <code className="font-mono">.env</code> (for example{" "}
        <code className="font-mono">CC_CRITIC_MODEL</code>), not here. Why the critic is a
        different model: <code className="font-mono">docs/evaluation-critic.md</code>.
      </p>
    </div>
  )
}
function ModelRow({
  model,
  selected,
  busy,
  locked,
  onChoose,
}: {
  model: ModelOption
  selected: boolean
  busy: boolean
  /** True for anyone but the operator: shown, not choosable. */
  locked: boolean
  onChoose: () => void
}) {
  return (
    <button
      type="button"
      onClick={onChoose}
      aria-pressed={selected}
      disabled={busy || locked}
      className={cn(
        "flex w-full items-start gap-3 rounded-xl border px-4 py-3 text-left transition-all duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        "disabled:cursor-default disabled:hover:translate-y-0",
        selected
          ? "border-primary/50 bg-primary/5 shadow-sm"
          : "border-border/70 bg-card/40 hover:-translate-y-0.5 hover:border-border hover:bg-card/70",
      )}
    >
      <span
        className={cn(
          "mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg border",
          selected ? "border-primary/40 bg-primary/10 text-primary" : "text-muted-foreground",
        )}
      >
        {busy ? (
          <Loader2 className="size-4 animate-spin" aria-hidden />
        ) : selected ? (
          <Check className="size-4" aria-hidden />
        ) : (
          <Cpu className="size-4" aria-hidden />
        )}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{model.label}</span>
          <Badge variant="outline">{model.provider}</Badge>
          {selected ? <Badge>In use</Badge> : null}
        </span>
        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">{model.id}</span>
        <span className="mt-1 block text-sm text-muted-foreground">{model.note}</span>
      </span>

      <span className="shrink-0 text-right text-xs text-muted-foreground">
        <span className="block">${model.inputPerMtok.toFixed(2)} in</span>
        <span className="block">${model.outputPerMtok.toFixed(2)} out</span>
        <span className="mt-0.5 block opacity-70">per M tokens</span>
      </span>
    </button>
  )
}

function Row({ term, value }: { term: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/40 pb-1.5">
      <dt className="text-muted-foreground">{term}</dt>
      <dd className="truncate font-mono text-xs">{value}</dd>
    </div>
  )
}

/**
 * A slider and a number, committing on release rather than on every pixel.
 *
 * Each commit is a request that clears the settings cache server-side, so a
 * slider that saved while it was being dragged would fire dozens of them for
 * one decision.
 */
function NumberField({
  label,
  hint,
  value,
  min,
  max,
  step,
  format,
  busy,
  locked,
  onCommit,
}: {
  label: string
  hint: string
  value: number
  min: number
  max: number
  step: number
  format: (value: number) => string
  busy: boolean
  locked: boolean
  onCommit: (value: number) => void
}) {
  const [local, setLocal] = useState(value)

  // The server is the source of truth: a rejected or adjusted value has to win
  // over whatever the slider is showing. Done during render by remembering the
  // value last seen, which is React's own pattern for "reset local state when a
  // prop changes" - an effect would paint the stale value first, then fix it.
  const [seen, setSeen] = useState(value)
  if (seen !== value) {
    setSeen(value)
    setLocal(value)
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={`field-${label}`} className="text-sm text-muted-foreground">
          {label}
        </label>
        <span className="font-mono text-xs">
          {format(local)}
          {busy ? <Loader2 className="ml-1.5 inline h-3 w-3 animate-spin" aria-hidden /> : null}
        </span>
      </div>
      <input
        id={`field-${label}`}
        type="range"
        min={min}
        max={max}
        step={step}
        value={local}
        disabled={busy || locked}
        onChange={(event) => setLocal(Number(event.target.value))}
        onPointerUp={() => local !== value && onCommit(local)}
        onKeyUp={() => local !== value && onCommit(local)}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full bg-border accent-primary disabled:opacity-50"
      />
      <p className="text-xs leading-relaxed text-muted-foreground">{hint}</p>
    </div>
  )
}
